import React from "react";
import {
	Home as HomeIcon,
	CheckCircle as CheckCircleIcon,
	Login as LoginIcon,
	Person as PersonIcon,
	Transform as TransformIcon,
	Terminal as TerminalIcon,
	Password as PasswordIcon,
	BugReport as BugReportIcon,
	ErrorOutline as ErrorOutlineIcon,
	SkipNext as SkipNextIcon,
	Link as LinkIcon,
	AccountTree as AccountTreeIcon,
	ContentCopy as ContentCopyIcon,
	Assessment as AssessmentIcon,
	Schedule as ScheduleIcon,
	Construction as ConstructionIcon,
	Code as CodeIcon,
	DirectionsCar,
	Map as MapIcon,
	TableRows,
} from "@mui/icons-material";
import type { AccessLevel } from "@domain/Auth";
import { ACTION_ICONS } from "@gui/widgets/icons";

/**
 * The route registry: every page's path, title, access level and navigation slot, in one table.
 * `App` generates its `<Route>`s from it, the sidebar / Home grid / account menu / issue counts
 * read their links from it (`navigation.ts`), and the toolbar + tab title come from it
 * (`routeTitle.ts`). Adding a page is one entry here plus its element in `routeElements.tsx`
 * (typed `Record<RouteId, …>`, so a missing element fails `tsc`).
 *
 * The elements live in a separate map rather than on each entry because this table is imported by
 * modules the pages themselves import (issue counts, the toolbar): a page component on an entry
 * would close an import cycle (Home → IssueCountsContext → this table → Home).
 */

/** Sidebar / menu sections. `account` is the toolbar's top-right person menu, not the sidebar. */
export type NavSection = "main" | "admin" | "developer" | "account";

/** Collapsible sidebar groups. A menu sits in its section at the position of its first member. */
export type NavMenuId = "issues" | "utilities" | "developer";

export const NAV_MENUS: Record<NavMenuId, { label: string; icon: React.ReactNode }> = {
	issues: { label: "Issues", icon: <BugReportIcon /> },
	utilities: { label: "Utilities", icon: <ConstructionIcon /> },
	developer: { label: "Developer", icon: <CodeIcon /> },
};

interface RouteNav {
	section: NavSection;
	/** Sort key within the section. */
	order: number;
	icon: React.ReactNode;
	/** Link text, when it differs from the page title (the sidebar is terser than the toolbar). */
	label?: string;
	menu?: NavMenuId;
	/** Who sees the link, when that differs from who may open the page — e.g. Job Detail is open
	 *  to every user (customers reach it from the dashboard) but only listed for admins. */
	access?: AccessLevel;
}

/** A retired URL that still resolves. `to` is a path pattern whose `:params` are filled from the
 *  legacy URL's (default: the entry's own path); `search` is appended. */
interface LegacyRedirect {
	from: string;
	to?: string;
	search?: string;
}

export interface RouteDef {
	id: string;
	/** Absolute path pattern. */
	path: string;
	/** Further patterns, relative to `path`, rendering the same page (`":jobId"`). They inherit the
	 *  title and access level. */
	subPaths?: readonly string[];
	title: string;
	access: AccessLevel;
	nav?: RouteNav;
	redirectsFrom?: readonly LegacyRedirect[];
}

const ROUTES = [
	// ── Public / account ──────────────────────────────────────────────────────────────────────
	{ id: "root", path: "/", title: "Login", access: "guestOnly" },
	{
		id: "login",
		path: "/login",
		title: "Login",
		access: "guestOnly",
		nav: { section: "main", order: 0, icon: <LoginIcon /> },
	},
	{ id: "register", path: "/register", title: "Register", access: "public" },
	{ id: "registrationNotice", path: "/registration-notice", title: "Registration Submitted", access: "public" },
	{ id: "forgotPassword", path: "/forgot-password", title: "Forgot Password", access: "public" },
	{ id: "resetPassword", path: "/reset-password", title: "Reset Password", access: "public" },
	{ id: "logout", path: "/logout", title: "Logout", access: "public" },
	{
		id: "home",
		path: "/home",
		title: "Rogo Portal",
		access: "public",
		nav: { section: "main", order: 10, icon: <HomeIcon />, label: "Home", access: "user" },
	},
	{
		id: "profile",
		path: "/profile",
		title: "Profile",
		access: "user",
		nav: { section: "account", order: 10, icon: <PersonIcon /> },
	},

	// ── Jobs ──────────────────────────────────────────────────────────────────────────────────
	{
		id: "jobsDashboard",
		path: "/jobs/dashboard",
		title: "Jobs Dashboard",
		access: "user",
		nav: { section: "main", order: 20, icon: <TableRows />, label: "Jobs" },
	},
	{
		id: "submitJobs",
		path: "/jobs/submit",
		title: "Submit Jobs",
		access: "user",
		nav: { section: "main", order: 30, icon: <ACTION_ICONS.upload /> },
	},
	{
		id: "markJobsReady",
		path: "/jobs/ready",
		title: "Mark Jobs Ready",
		access: "user",
		nav: { section: "main", order: 40, icon: <CheckCircleIcon /> },
	},
	{
		// Bare /jobs is Job Detail with no job yet: admins pick one from the toolbar's job search, which is also the way
		// in, so it has no sidebar link or Home card.
		id: "jobDetail",
		path: "/jobs",
		subPaths: [":jobId"],
		title: "Job Details",
		access: "user",
		redirectsFrom: [
			// Job Diagnostics merged into Job Detail.
			{ from: "/admin/job-diagnostics" },
			{ from: "/admin/job-diagnostics/:jobId", to: "/jobs/:jobId" },
		],
	},

	// ── Admin ─────────────────────────────────────────────────────────────────────────────────
	{
		id: "robots",
		path: "/admin/robots",
		subPaths: [":robotId", ":robotId/:shiftId"],
		title: "Robots",
		access: "admin",
		nav: { section: "admin", order: 10, icon: <DirectionsCar /> },
	},
	{
		id: "scheduling",
		path: "/admin/scheduling",
		title: "Scheduling",
		access: "admin",
		nav: { section: "admin", order: 20, icon: <ScheduleIcon /> },
	},
	{
		id: "jobErrors",
		path: "/admin/job-errors",
		subPaths: [":jobId"],
		title: "Job Errors",
		access: "admin",
		nav: { section: "admin", order: 40, icon: <ErrorOutlineIcon />, menu: "issues" },
		redirectsFrom: [
			// Data Sendback Issues and Exe Mission Validation became Job Errors tabs.
			{ from: "/admin/data-sendback-issues", search: "?tab=dataSendback" },
			{ from: "/admin/exe-mission-validation", search: "?tab=exe" },
			{ from: "/admin/exe-mission-validation/:jobId", to: "/admin/job-errors/:jobId", search: "?tab=exe" },
		],
	},
	{
		id: "skips",
		path: "/admin/skips",
		title: "Skips",
		access: "admin",
		nav: { section: "admin", order: 41, icon: <SkipNextIcon />, menu: "issues" },
	},
	{
		id: "dealMatcher",
		path: "/admin/deal-matcher",
		title: "Deal Matcher",
		access: "admin",
		nav: { section: "admin", order: 42, icon: <LinkIcon />, menu: "issues" },
	},
	{
		id: "branchMatcher",
		path: "/admin/branch-matcher",
		title: "Branch Matcher",
		access: "admin",
		nav: { section: "admin", order: 43, icon: <AccountTreeIcon />, menu: "issues" },
	},
	{
		id: "passwords",
		path: "/admin/passwords",
		title: "Passwords",
		access: "admin",
		nav: { section: "admin", order: 50, icon: <PasswordIcon /> },
		// The two password pages merged into Passwords.
		redirectsFrom: [{ from: "/admin/mission-control-passwords-generator" }, { from: "/admin/password-generator" }],
	},
	{
		id: "dealCreator",
		path: "/admin/deal-creator",
		title: "Deal Creator",
		access: "admin",
		nav: { section: "admin", order: 60, icon: <ContentCopyIcon /> },
	},
	{
		id: "companySettings",
		path: "/admin/company-settings",
		title: "Company Settings",
		access: "admin",
		nav: { section: "admin", order: 70, icon: <ACTION_ICONS.settings /> },
		redirectsFrom: [{ from: "/admin/import" }],
	},
	{
		id: "geoFileConverter",
		path: "/admin/geofile-converter",
		title: "GeoFile Converter",
		access: "admin",
		nav: { section: "admin", order: 80, icon: <TransformIcon />, menu: "utilities" },
	},
	{
		id: "missionReport",
		path: "/admin/mission-report",
		title: "Mission Report",
		access: "admin",
		nav: { section: "admin", order: 81, icon: <AssessmentIcon />, menu: "utilities" },
	},
	{
		id: "missionControlLogs",
		path: "/admin/mission-control-logs",
		title: "Mission Control Logs",
		access: "admin",
		nav: { section: "admin", order: 82, icon: <TerminalIcon />, label: "App Logs", menu: "utilities" },
	},

	// ── Developer ─────────────────────────────────────────────────────────────────────────────
	{
		id: "agrianIssues",
		path: "/admin/agrian-issues",
		title: "Agrian Issues",
		access: "developer",
		nav: { section: "developer", order: 10, icon: <BugReportIcon />, label: "Agrian", menu: "developer" },
	},
	{
		id: "developerMap",
		path: "/admin/developer-map",
		title: "Developer Map",
		access: "developer",
		nav: { section: "developer", order: 20, icon: <MapIcon />, menu: "developer" },
	},
	{
		id: "mapmaking",
		path: "/admin/mapmaking",
		title: "Mapmaking",
		access: "developer",
		nav: { section: "developer", order: 30, icon: <MapIcon />, menu: "developer" },
	},
] as const satisfies readonly RouteDef[];

export type RouteId = (typeof ROUTES)[number]["id"];

/** The registry, widened for iteration (the literal `ROUTES` type is a union of entry shapes). */
export const ROUTE_DEFS: readonly (RouteDef & { id: RouteId })[] = ROUTES;

/** Typed builders for the routes that take parameters. Adopt where a call site touches them. */
export const routePaths = {
	jobDetail: (jobId: string) => `/jobs/${encodeURIComponent(jobId)}`,
	jobErrors: (jobId?: string) => (jobId ? `/admin/job-errors/${encodeURIComponent(jobId)}` : "/admin/job-errors"),
	robots: (robotId?: string | null, shiftId?: string | null) => {
		if (!robotId) return "/admin/robots";
		const robot = `/admin/robots/${encodeURIComponent(robotId)}`;
		return shiftId ? `${robot}/${encodeURIComponent(shiftId)}` : robot;
	},
};
